import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BuddyInstanceInterface } from '../../../models/interface/buddy/buddy-instance.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { DiaryInstanceInterface } from '../../../models/interface/diary-instance.interface';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { BehaviorSubject, Observable, skip, Subscription, take } from 'rxjs';
import { HelperActivityService } from '../../../services/helper/helper-activity/helper-activity.service';
import { BuddyActivityInterface } from '../../../models/interface/buddy/buddy-activity.interface';
import { BuddyStore } from '../../../store/buddy/component-store/buddy.store';
import { faBell } from '@fortawesome/free-regular-svg-icons/faBell';
import { faClock } from '@fortawesome/free-regular-svg-icons/faClock';

type PagedActivity = {
  pair1: BuddyActivityInterface;
  pair2: BuddyActivityInterface;
  localizedDate: string;
};

export type DialogBuddyInstanceDetailData = {
  buddyInstance: BuddyInstanceInterface,
  members: UserInterface[],
  studyCollaborators: UserInterface[],
  interventions: InterventionInterface[],
  interventionInstances: InterventionInstanceInterface[],
  diaryInstances: DiaryInstanceInterface[],
  diaries: DiaryInterface[],
  isManager: boolean,
  studyId: number,
};

@Component({
  selector: 'app-dialog-buddy-instance-detail',
  templateUrl: './dialog-buddy-instance-detail.component.html',
  styleUrls: ['./dialog-buddy-instance-detail.component.css'],
  providers: [BuddyStore],
})
export class DialogBuddyInstanceDetailComponent implements OnInit, OnDestroy {
  // Icons
  faBell = faBell;
  faClock = faClock;

  public buddyInstance: BuddyInstanceInterface;
  public studyMembers: UserInterface[] = [];
  public interventions: InterventionInterface[];
  public interventionInstances: InterventionInstanceInterface[] = [];
  public diaryInstances: DiaryInstanceInterface[] = [];
  public diaries: DiaryInterface[] = [];
  public studyCollaborators: UserInterface[] = [];
  public isManager: boolean;

  public activities: BuddyActivityInterface[] = [];
  public pagedActivitySubject = new BehaviorSubject<PagedActivity[]>([]);
  public pagedActivity$ = this.pagedActivitySubject.asObservable();

  public buddyActivities$: Observable<BuddyActivityInterface[]>;

  public buddyActivitiesPair: PagedActivity[] = [];

  public isLoadingActivities$: Observable<boolean>;

  public studyId: number;

  private isLoadingActivitiesSubject = new BehaviorSubject<boolean>(true);

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogBuddyInstanceDetailData,
    private helperService: HelperService,
    private buddyStore: BuddyStore,
    private helperActivityService: HelperActivityService,
  ) {
    this.isLoadingActivities$ = this.isLoadingActivitiesSubject.asObservable();
    this.buddyActivities$ = this.buddyStore.allBuddyActivitiesOfBuddy$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  ngOnInit(): void {
    this.buddyInstance = this.data.buddyInstance;
    this.studyMembers = this.data.members;
    this.interventions = this.data.interventions;
    this.interventionInstances = this.data.interventionInstances;
    this.diaryInstances = this.data.diaryInstances;
    this.diaries = this.data.diaries;
    this.studyCollaborators = this.data.studyCollaborators;
    this.isManager = this.data.isManager;
    this.studyId = this.data.studyId;

    if (this.isManager) {
      this.buddyStore.getBuddyActivitiesEM(this.buddyInstance.id);
    } else {
      this.buddyStore.getBuddyActivities(this.buddyInstance.id);
    }

    this.subscriptions.push(
      this.buddyActivities$.pipe(skip(1), take(1)).subscribe({
        next: (result) => {
          this.activities = result;
          this.activities = this.helper.getBuddyActivityByNewest(this.activities);
          this.pairBuddy();
          this.pagedActivitySubject.next(this.buddyActivitiesPair.slice(0, 20));
          this.isLoadingActivitiesSubject.next(false);
        },
        error: () => {
          this.activities = [];
          this.pagedActivitySubject.next([]);
          this.isLoadingActivitiesSubject.next(false);
        },
      })
    );
  }

  public updatePagedActivities(event: PagedActivity[]) {
    if (event) {
      this.pagedActivitySubject.next(event);
    }
  }

  public hasSentReminder(buddyActivityPair: PagedActivity): boolean {
    const reminder1 = buddyActivityPair?.pair1?.attributes.daily_reminder_timestamps;
    const reminder2 = buddyActivityPair?.pair2?.attributes.daily_reminder_timestamps;
    return !!reminder1 || !!reminder2;
  }

  public getBuddyState(buddyActivityPair: PagedActivity) {
    const task1 = buddyActivityPair?.pair1?.attributes.daily_task_timestamps;
    const task2 = buddyActivityPair?.pair2?.attributes.daily_task_timestamps;
    if (!!task1 && !!task2) {
      return 'both_daily_task_completed';
    } else if (!!task1 || !!task2) {
      return 'only_one_daily_task_completed';
    } else {
      return 'no_daily_task_completed';
    }
  }

  getClassForActivity(activity: PagedActivity) {
    const state = this.getBuddyState(activity);
    switch (state) {
      case 'no_daily_task_completed':
        return 'text-danger';
      case 'only_one_daily_task_completed':
        return 'text-info';
      case 'both_daily_task_completed':
        return 'text-success';
    }
  }

  public getBuddySingleState(pair: BuddyActivityInterface) {
    const task = pair.attributes.daily_task_timestamps;
    const reminder = pair.attributes.daily_reminder_timestamps;
    if (!!task && !!reminder) {
      return 'one_daily_task_completed_with_reminder';
    } else if (!!task) {
      return 'one_daily_task_completed';
    } else {
      return 'no_daily_task_completed';
    }
  }

  getClassForSingleActivity(activity: BuddyActivityInterface) {
    const state = this.getBuddySingleState(activity);
    switch (state) {
      case 'no_daily_task_completed':
        return 'text-danger';
      case 'one_daily_task_completed':
      case 'one_daily_task_completed_with_reminder':
        return 'text-success';
    }
  }

  public pairBuddy(): void {
    const buddyActivitiesPair: PagedActivity[] = [];
    this.activities.forEach((activity) => {
      const localizeDate = this.helper.localizeDate(activity.attributes.created_at);
      if (buddyActivitiesPair.every((pair) => pair.localizedDate.toString() !== localizeDate.toString())) {
        const buddyAct = this.activities.find(
          (buddyActivity) =>
            buddyActivity.attributes.user_id.toString() !== activity.attributes.user_id.toString() &&
            this.helper.localizeDate(buddyActivity.attributes.created_at) === localizeDate
        );
        buddyActivitiesPair.push({
          pair1: activity,
          pair2: buddyAct,
          localizedDate: localizeDate,
        });
      }
    });
    this.buddyActivitiesPair = buddyActivitiesPair;
  }

  private getMissingUser(availableUser: number) {
    const user0 = this.buddyInstance.attributes.buddies_ids[0];
    const user1 = this.buddyInstance.attributes.buddies_ids[1];
    let buddy = 'System';
    if (availableUser == user0) {
      if (this.helper.findArrObjById(user1, this.studyMembers) !== null) {
        buddy = this.helper.getCodeNameEmail(user1, this.studyMembers);
      }
    } else if (availableUser == user1) {
      if (this.helper.findArrObjById(user0, this.studyMembers) !== null) {
        buddy = this.helper.getCodeNameEmail(user0, this.studyMembers);
      }
    }
    return { buddy };
  }

  getMissingUserPair(activity: BuddyActivityInterface) {
    const availableUser = activity?.attributes?.user_id;
    if (!availableUser) {
      return { buddy: 'System' };
    }
    return this.getMissingUser(availableUser);
  }

  public getTranslation(userId?: number): { buddy: string } {
    if (userId === undefined || this.helper.findArrObjById(userId, this.studyMembers) === null) {
      return { buddy: 'System' };
    }
    return { buddy: this.helper.getCodeNameEmail(userId, this.studyMembers) };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
